import { useEffect, useState } from 'react';
import { Box, Button, Group, Modal, Stack, Switch, Text, TextInput } from '@mantine/core';
import { RoleSection, SelectedRoles, ModalCloseButton } from './roleEditorShared';
import { BASE_ADMIN, BASE_MEMBER, MODAL_STYLES } from './roleEditorLogic';

export interface InvitedUser {
  email: string;
  name: string;
  roles: string[];
  status: 'Invited';
  last_activity: string;
}

interface InviteUserModalProps {
  opened: boolean;
  onClose: () => void;
  onInvite: (user: InvitedUser) => void;
}

const BASE_ROLES = [BASE_MEMBER, BASE_ADMIN];

function deriveNameFromEmail(email: string): string {
  const local = email.split('@')[0];
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteUserModal({ opened, onClose, onInvite }: Readonly<InviteUserModalProps>) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [role, setRole] = useState(BASE_MEMBER);

  // Reset state whenever the modal opens
  useEffect(() => {
    const callFn = () => {
      setEmail('');
      setEmailError('');
      setRole(BASE_MEMBER);
    }
    if (opened) {
      callFn();
    }
  }, [opened]);

  function handleClose() {
    onClose();
  }

  function handleInvite() {
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    const name = deriveNameFromEmail(trimmed);
    onInvite({ email: trimmed, name, roles: [role], status: 'Invited', last_activity: '—' });
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      withCloseButton={false}
      size={540}
      radius="lg"
      padding={0}
      styles={MODAL_STYLES}
    >
      <>
        {/* Header */}
        <Group
          px={22}
          py={20}
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
          wrap="nowrap"
          align="flex-start"
        >
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text fz="md" fw={700} lh="normal">Invite user</Text>
            <Text fz="xs" c="var(--text-muted)" lh="normal">
              Send an invitation to join your organisation.
            </Text>
          </Stack>
          <ModalCloseButton onClick={handleClose} />
        </Group>

        {/* Body */}
        <Stack px={22} pt={6} pb={18} gap={0} style={{ overflowY: 'auto', maxHeight: 'calc(88vh - 140px)' }}>
          {/* Email field */}
          <Box py={15} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <Text fz="sm" fw={700} mb={7}>Email address</Text>
            <TextInput
              placeholder="name@company.com"
              type="email"
              autoFocus
              value={email}
              data-input-cta
              styles={{
                label: {
                  marginBottom: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 'normal',
                },
                input: {
                  '--input-size': '2.5rem',
                },
                error: {
                  marginTop: 6,
                  color: 'var(--status-danger)',
                  lineHeight: 'normal',
                },
              }}
              onChange={e => {
                setEmail(e.currentTarget.value);
                if (emailError) setEmailError('');
              }}
              onKeyDown={e => { if (e.key === 'Enter') handleInvite(); }}
              error={emailError || undefined}

            />
          </Box>

          {/* Organisation role */}
          <RoleSection
            title="Organisation administrator"
            desc="Full access to the Control Panel - users, roles, entitlements & tokens."
            noBorder
          >
            <Switch
              checked={role === BASE_ADMIN}
              withThumbIndicator={false}
              style={{ '--mantine-color-dark-5': 'var(--border-strong)' }}
              onChange={e => setRole(e.target.checked ? BASE_ADMIN : BASE_MEMBER)}
              color="var(--brand-accent)"
              mt={2}
            />
          </RoleSection>

          <SelectedRoles
            selected={[role]}
            available={BASE_ROLES.map(slug => ({ slug, name: slug.charAt(0).toUpperCase() + slug.slice(1) }))}
            emptyText="No roles yet — assign access above."
          />
        </Stack>

        {/* Footer */}
        <Group
          justify="flex-end"
          gap={10}
          px={22}
          py={16}
          style={{ borderTop: '1px solid var(--border-subtle)' }}
        >
          <Button
            variant="default"
            onClick={handleClose}
            style={{
              background: 'var(--bg-surface)',
              '--button-color': 'var(--text-muted)',
              '--button-hover-color': 'var(--accent-text)',
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleInvite}>
            Send invitation
          </Button>
        </Group>
      </>
    </Modal>
  );
}
